import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { ConfirmDialog } from '@/components/overlay/ConfirmDialog';
import { ConfirmContext, type ConfirmFn, type ConfirmOptions } from './confirmContext';

interface DialogState extends Required<Omit<ConfirmOptions, 'description'>> {
  open: boolean;
  description?: ReactNode;
}

const defaultState: DialogState = {
  open: false,
  title: 'Are you sure?',
  description: undefined,
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  tone: 'primary',
};

/**
 * Provides a promise-based `confirm()` so any component can request a
 * confirmation dialog without rendering one. Replaces the ~30 bespoke
 * "DeleteX" components in the legacy codebase.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DialogState>(defaultState);
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    setState({ ...defaultState, ...options, open: true });
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = useCallback((result: boolean) => {
    resolver.current?.(result);
    resolver.current = null;
    setState((s) => ({ ...s, open: false }));
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext value={value}>
      {children}
      <ConfirmDialog
        open={state.open}
        title={state.title}
        description={state.description}
        confirmLabel={state.confirmLabel}
        cancelLabel={state.cancelLabel}
        tone={state.tone}
        onConfirm={() => close(true)}
        onCancel={() => close(false)}
      />
    </ConfirmContext>
  );
}
